
import api from "@/auth/api";

// ============================================================
// POS SESSION SERVICE
// Backend: PosSessionsController
// Route: api/pos/sessions
// ============================================================

const SESSION_BASE_URL = "/pos/sessions";

// ============================================================
// VALIDATE IDENTIFIER
// ============================================================

function requiredId(value, label) {
  const id = String(value ?? "").trim();

  if (!id) {
    throw new Error(`${label} is required.`);
  }

  return encodeURIComponent(id);
}

// ============================================================
// POS COMPUTER TIMEZONE
// ============================================================

function getComputerTimeZone() {
  const timeZone = String(
    Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone ?? ""
  ).trim();

  if (!timeZone) {
    throw new Error(
      "The POS computer timezone is required."
    );
  }

  return timeZone;
}

// ============================================================
// RESOLVE REQUEST TIMEZONE
// ============================================================

function resolveTimeZone(value) {
  const timeZone = String(
    value ?? getComputerTimeZone()
  ).trim();

  if (!timeZone) {
    throw new Error(
      "The POS computer timezone is required."
    );
  }

  try {
    new Intl.DateTimeFormat("en-US", {
      timeZone
    });
  } catch {
    throw new Error(
      "The POS computer timezone is invalid."
    );
  }

  return timeZone;
}

// ============================================================
// GET ALL SESSIONS
// GET /api/pos/sessions
// ============================================================

async function getSessions() {
  const response = await api.get(
    SESSION_BASE_URL
  );

  return response.data;
}

// ============================================================
// GET SESSION BY ID
// GET /api/pos/sessions/{id}
// ============================================================

async function getSession(id) {
  const response = await api.get(
    `${SESSION_BASE_URL}/${requiredId(id, "POS session ID")}`
  );

  return response.data;
}

// ============================================================
// GET CURRENT USER SESSION
// GET /api/pos/sessions/current?timeZone=...
// ============================================================

async function getCurrentSession() {
  const timeZone = getComputerTimeZone();

  const response = await api.get(
    `${SESSION_BASE_URL}/current`,
    {
      params: {
        timeZone
      }
    }
  );

  return response.data;
}

// ============================================================
// GET CURRENT TERMINAL SESSION
// GET /api/pos/sessions/terminal/{terminalId}/current
// ============================================================

async function getCurrentTerminalSession(terminalId) {
  const id = requiredId(
    terminalId,
    "POS terminal ID"
  );

  const timeZone = getComputerTimeZone();

  const response = await api.get(
    `${SESSION_BASE_URL}/terminal/${id}/current`,
    {
      params: {
        timeZone
      }
    }
  );

  return response.data;
}

// ============================================================
// OPEN SESSION
// POST /api/pos/sessions/open
// ============================================================

async function openSession(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error(
      "A valid POS session request is required."
    );
  }

  // ----------------------------------------------------------
  // TERMINAL
  // ----------------------------------------------------------

  const terminalId =
    String(payload.terminalId ?? "").trim();

  if (!terminalId) {
    throw new Error(
      "POS terminal ID is required to start a session."
    );
  }

  // ----------------------------------------------------------
  // CURRENCY
  // ----------------------------------------------------------

  const currencyCode =
    String(payload.currencyCode ?? "")
      .trim()
      .toUpperCase();

  if (!/^[A-Z]{3}$/.test(currencyCode)) {
    throw new Error(
      "A valid three-letter session currency is required."
    );
  }

  // ----------------------------------------------------------
  // OPENING CASH
  // ----------------------------------------------------------

  if (
    payload.openingCashAmount === null ||
    payload.openingCashAmount === undefined ||
    payload.openingCashAmount === ""
  ) {
    throw new Error(
      "Opening cash amount is required."
    );
  }

  const openingCashAmount =
    Number(payload.openingCashAmount);

  if (
    !Number.isFinite(openingCashAmount) ||
    openingCashAmount < 0
  ) {
    throw new Error(
      "Opening cash amount must be non-negative."
    );
  }

  if (openingCashAmount > 999999999999.99) {
    throw new Error(
      "Opening cash amount exceeds the permitted maximum."
    );
  }

  // ----------------------------------------------------------
  // NOTES
  // ----------------------------------------------------------

  const notes =
    payload.notes === null ||
    payload.notes === undefined
      ? null
      : String(payload.notes).trim() || null;

  if (notes && notes.length > 1000) {
    throw new Error(
      "Session notes cannot exceed 1000 characters."
    );
  }

  // ----------------------------------------------------------
  // POS COMPUTER TIMEZONE
  // ----------------------------------------------------------

  const timeZone = resolveTimeZone(
    payload.timeZone
  );

  // ----------------------------------------------------------
  // REQUEST PAYLOAD
  // ----------------------------------------------------------

  const requestPayload = {
    terminalId,
    currencyCode,
    openingCashAmount,
    notes,
    timeZone
  };

  // ----------------------------------------------------------
  // OPEN SESSION
  // ----------------------------------------------------------

  const response = await api.post(
    `${SESSION_BASE_URL}/open`,
    requestPayload
  );

  return response.data;
}

// ============================================================
// PREVIEW SESSION CLOSING
// GET /api/pos/sessions/{id}/close-preview
// ============================================================

async function previewClose(
  sessionId,
  closingCashAmount
) {
  const id = requiredId(
    sessionId,
    "POS session ID"
  );

  if (
    closingCashAmount === null ||
    closingCashAmount === undefined ||
    closingCashAmount === ""
  ) {
    throw new Error(
      "Closing cash amount is required."
    );
  }

  const amount = Number(closingCashAmount);

  if (
    !Number.isFinite(amount) ||
    amount < 0
  ) {
    throw new Error(
      "Closing cash amount must be non-negative."
    );
  }

  const response = await api.get(
    `${SESSION_BASE_URL}/${id}/close-preview`,
    {
      params: {
        closingCashAmount: amount
      }
    }
  );

  return response.data;
}

// ============================================================
// CLOSE SESSION
// POST /api/pos/sessions/{id}/close
// ============================================================

async function closeSession(sessionId, payload) {
  const id = requiredId(
    sessionId,
    "POS session ID"
  );

  if (!payload || typeof payload !== "object") {
    throw new Error(
      "A valid POS session closing request is required."
    );
  }

  if (
    payload.closingCashAmount === null ||
    payload.closingCashAmount === undefined ||
    payload.closingCashAmount === ""
  ) {
    throw new Error(
      "Closing cash amount is required."
    );
  }

  const closingCashAmount =
    Number(payload.closingCashAmount);

  if (
    !Number.isFinite(closingCashAmount) ||
    closingCashAmount < 0
  ) {
    throw new Error(
      "Closing cash amount must be non-negative."
    );
  }

  const notes =
    payload.notes === null ||
    payload.notes === undefined
      ? null
      : String(payload.notes).trim() || null;

  const response = await api.post(
    `${SESSION_BASE_URL}/${id}/close`,
    {
      closingCashAmount,
      notes
    }
  );

  return response.data;
}

// ============================================================
// EXPORT
// ============================================================

export default {
  getSessions,
  getSession,
  getCurrentSession,
  getCurrentTerminalSession,
  openSession,
  previewClose,
  closeSession
};
